/**
 * Random sensory effects catalog — see `docs/16_random_sensory_effects.md`.
 *
 * Alpha set (Visual Sprint 2): three effects only.
 *   - `spark_color_warm`     — Spark hue shifts to amber for 3s
 *   - `spark_breath_deeper`  — Spark breath amplitude × 1.3 for 6s
 *   - `bg_star_twinkle`      — one network star pulses brighter for 2s
 *
 * Each effect has:
 *   - `baseProb`: rough probability of firing on any given tap, before
 *     cooldowns. Tuned by simulation in `__tests__/catalog.test.ts`.
 *   - `cooldownSameMs`: minimum ms between firings of THIS effect.
 *   - `cooldownClassMs`: minimum ms between any two effects of this class.
 *   - `durationMs`: how long the effect is "active" once fired.
 *   - `class`: visual / audio / haptic / multi (see doc 16 §6.1).
 *
 * Effects with `epochMin` only become eligible once the player reaches
 * that epoch.
 *
 * Anti-fatigue is enforced in the scheduler, not here — this file just
 * declares what exists.
 */
export type EffectClass = 'visual' | 'audio' | 'haptic' | 'multi';

export interface EffectDefinition {
  readonly id: string;
  readonly class: EffectClass;
  /** Rough per-tap probability (before cooldowns). 0..1. */
  readonly baseProb: number;
  /** Cooldown for the same effect id. */
  readonly cooldownSameMs: number;
  /** Cooldown for any effect of this class. */
  readonly cooldownClassMs: number;
  /** How long the effect "stays active" once emitted. */
  readonly durationMs: number;
  /** Minimum epoch (1-indexed) for this effect to be eligible. */
  readonly epochMin?: number;
}

export const ALPHA_EFFECTS: readonly EffectDefinition[] = [
  {
    id: 'spark_color_warm',
    class: 'visual',
    baseProb: 1 / 250,
    cooldownSameMs: 10 * 60_000, // 10 min
    cooldownClassMs: 30_000, // 30s between visuals
    durationMs: 3_000,
  },
  {
    id: 'spark_breath_deeper',
    class: 'visual',
    baseProb: 1 / 180,
    cooldownSameMs: 8 * 60_000,
    cooldownClassMs: 30_000,
    durationMs: 6_000,
  },
  {
    id: 'bg_star_twinkle',
    class: 'visual',
    baseProb: 1 / 90,
    cooldownSameMs: 90_000, // 1.5 min — twinkles are cheap, can be more frequent
    cooldownClassMs: 30_000,
    durationMs: 2_000,
  },
  // ----- Atmospheric breath (doc 20 §4, doc 22 §7) -----
  // Random "breath of the world" between ambient and foreground. Plays
  // independently of tap rhythm — so even an idle player hears the
  // game stay alive. 25 file variants in `/sounds/ambient/atmospheric_breath/`,
  // all tuned to A minor. The audio player applies ducking when higher
  // tiers play, so this gets out of the way during peaks.
  {
    id: 'atmospheric_breath_fire',
    class: 'audio',
    // ~1/40 per tap chance: at 5 taps/min the breath fires once every
    // ~8 minutes from taps alone. The scheduler also auto-fires every
    // 8-20 sec when idle (see store integration below).
    baseProb: 1 / 40,
    cooldownSameMs: 10_000, // не два подряд за 10 сек
    cooldownClassMs: 6_000,
    durationMs: 8_000,
  },
];

export function findEffect(id: string): EffectDefinition | undefined {
  return ALPHA_EFFECTS.find((e) => e.id === id);
}
